import { useState } from "react";
import type { RunOverrides, RunPlan } from "@zenforge/shared";
import { useApi } from "../api.js";

/**
 * The confirmation step between choosing targets and queueing them.
 *
 * Shows the configuration the run will actually use, with the parts a person
 * may change. Defaults come from the server's own resolver rather than being
 * re-derived here: a client-side copy of the precedence rules would disagree
 * with what runs the moment one of them changed.
 *
 * Everything shown but not editable — prompts, documents, mappings — is shown
 * on purpose. Seeing the prompt you are about to run is useful even when it is
 * not yours to edit here.
 *
 * Targets are deliberately absent: they were chosen on the page behind this,
 * and repeating a long list would bury the settings this modal exists for. The
 * count alone is enough to confirm you are running what you meant to.
 */

interface StepPreview {
  stepOrder: number;
  name: string;
  stepType: "LLM" | "INTERNAL_TRANSFORM";
  transformType: string | null;
  enableRetrieval: boolean;
  hasPrompt: boolean;
  prompt: string | null;
  promptDocFilename: string | null;
  sampleMode: string;
  inputMappings: { varName: string; sourceType: string; stepOrder?: number }[] | null;
}

interface RunDefaults {
  plan: RunPlan;
  steps: StepPreview[];
}

function Changed() {
  return (
    <span className="badge mini info" style={{ marginLeft: 6 }}>
      changed
    </span>
  );
}

/** A number input that reports null when cleared, so "unset" stays expressible. */
function NumberField(props: {
  label: string;
  value: number | null;
  placeholder: number | null;
  step?: number;
  onChange: (v: number | null) => void;
  overridden: boolean;
}) {
  return (
    <div className="field" style={{ flex: 1, minWidth: 130 }}>
      <label>
        {props.label}
        {props.overridden && <Changed />}
      </label>
      <input
        type="number"
        step={props.step ?? 0.1}
        value={props.value ?? ""}
        placeholder={props.placeholder === null ? "not set" : String(props.placeholder)}
        onChange={(e) => props.onChange(e.target.value === "" ? null : Number(e.target.value))}
      />
    </div>
  );
}

export function RunPipelineModal(props: {
  collectionId: number;
  pipelineId: number;
  pipelineName: string;
  targetCount: number;
  models: string[];
  submitting: boolean;
  onRun: (overrides: RunOverrides) => void;
  onClose: () => void;
}) {
  const { collectionId, pipelineId, models, submitting, onRun, onClose } = props;
  const [overrides, setOverrides] = useState<RunOverrides>({});

  const defaults = useApi<RunDefaults>(
    `/pipelines/collection/${collectionId}/run-defaults/${pipelineId}`,
    [collectionId, pipelineId],
  );

  const plan = defaults.data?.plan;
  const steps = defaults.data?.steps ?? [];

  const setParam = (
    key: keyof NonNullable<RunOverrides["params"]>,
    value: number | string | null,
  ) => {
    const params = { ...(overrides.params ?? {}) };
    if (value === null || value === "") {
      delete params[key];
    } else {
      (params as Record<string, unknown>)[key] = value;
    }
    setOverrides({ ...overrides, params: Object.keys(params).length > 0 ? params : undefined });
  };

  const changedCount =
    (overrides.model !== undefined ? 1 : 0) +
    (overrides.autoApprove !== undefined ? 1 : 0) +
    (overrides.batchMode !== undefined ? 1 : 0) +
    Object.keys(overrides.params ?? {}).length;

  return (
    <div className="modal-backdrop" onClick={submitting ? undefined : onClose}>
      <div
        className="modal-card"
        style={{ maxWidth: 720, width: "94%", maxHeight: "88vh", overflowY: "auto" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="row between" style={{ marginBottom: 14 }}>
          <h3 style={{ margin: 0 }}>Run {props.pipelineName}</h3>
          <button type="button" className="btn mini" onClick={onClose} disabled={submitting}>
            ✕
          </button>
        </div>

        <p className="mini muted" style={{ marginTop: 0 }}>
          {props.targetCount} target{props.targetCount === 1 ? "" : "s"} selected. Anything
          changed here applies to this run only — the pipeline and the collection are left
          as they are.
        </p>

        {defaults.loading && <p className="muted mini">Loading configuration…</p>}
        {defaults.error && (
          <p className="muted mini">Could not load the configuration. Close and try again.</p>
        )}

        {plan && (
          <>
            <div className="row" style={{ gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
              <div className="field" style={{ flex: 1, minWidth: 190 }}>
                <label>
                  Model
                  {overrides.model !== undefined && <Changed />}
                </label>
                <select
                  value={overrides.model ?? ""}
                  onChange={(e) => setOverrides({ ...overrides, model: e.target.value || undefined })}
                >
                  <option value="">Default ({plan.model ?? "not set"})</option>
                  {models.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
                <small className="muted">Applies to every step in this run.</small>
              </div>

              <div className="field" style={{ flex: 1, minWidth: 190 }}>
                <label>
                  Approval
                  {overrides.autoApprove !== undefined && <Changed />}
                </label>
                <select
                  value={
                    overrides.autoApprove === undefined ? "" : overrides.autoApprove ? "auto" : "manual"
                  }
                  onChange={(e) =>
                    setOverrides({
                      ...overrides,
                      autoApprove: e.target.value === "" ? undefined : e.target.value === "auto",
                    })
                  }
                >
                  <option value="">Default ({plan.autoApprove ? "auto-approve" : "manual"})</option>
                  <option value="manual">📋 Manual approval</option>
                  <option value="auto">⚡ Auto-approve &amp; push</option>
                </select>
              </div>

              <div className="field" style={{ flex: 1, minWidth: 190 }}>
                <label>
                  Batch generation
                  {overrides.batchMode !== undefined && <Changed />}
                </label>
                <select
                  value={overrides.batchMode === undefined ? "" : overrides.batchMode ? "on" : "off"}
                  onChange={(e) =>
                    setOverrides({
                      ...overrides,
                      batchMode: e.target.value === "" ? undefined : e.target.value === "on",
                    })
                  }
                >
                  <option value="">Default ({plan.batchMode ? "on" : "off"})</option>
                  <option value="off">Immediate</option>
                  <option value="on">Batch API</option>
                </select>
              </div>
            </div>

            <details style={{ marginTop: 12 }}>
              <summary className="mini muted" style={{ cursor: "pointer" }}>
                Model parameters
              </summary>
              <div
                className="row"
                style={{ gap: 12, alignItems: "flex-start", flexWrap: "wrap", marginTop: 8 }}
              >
                <NumberField
                  label="Temperature"
                  value={overrides.params?.temperature ?? null}
                  placeholder={plan.params.temperature}
                  onChange={(v) => setParam("temperature", v)}
                  overridden={overrides.params?.temperature !== undefined}
                />
                <NumberField
                  label="Top P"
                  value={overrides.params?.topP ?? null}
                  placeholder={plan.params.topP}
                  onChange={(v) => setParam("topP", v)}
                  overridden={overrides.params?.topP !== undefined}
                />
                <NumberField
                  label="Frequency penalty"
                  value={overrides.params?.frequencyPenalty ?? null}
                  placeholder={plan.params.frequencyPenalty}
                  onChange={(v) => setParam("frequencyPenalty", v)}
                  overridden={overrides.params?.frequencyPenalty !== undefined}
                />
                <NumberField
                  label="Presence penalty"
                  value={overrides.params?.presencePenalty ?? null}
                  placeholder={plan.params.presencePenalty}
                  onChange={(v) => setParam("presencePenalty", v)}
                  overridden={overrides.params?.presencePenalty !== undefined}
                />
                <NumberField
                  label="Max tokens"
                  value={overrides.params?.maxTokens ?? null}
                  placeholder={plan.params.maxTokens}
                  step={1}
                  onChange={(v) => setParam("maxTokens", v)}
                  overridden={overrides.params?.maxTokens !== undefined}
                />
                <NumberField
                  label="Seed"
                  value={overrides.params?.seed ?? null}
                  placeholder={plan.params.seed}
                  step={1}
                  onChange={(v) => setParam("seed", v)}
                  overridden={overrides.params?.seed !== undefined}
                />
                <div className="field" style={{ flex: 1, minWidth: 150 }}>
                  <label>
                    Reasoning effort
                    {overrides.params?.reasoningEffort !== undefined && <Changed />}
                  </label>
                  <select
                    value={overrides.params?.reasoningEffort ?? ""}
                    onChange={(e) => setParam("reasoningEffort", e.target.value || null)}
                  >
                    <option value="">Default ({plan.params.reasoningEffort ?? "not set"})</option>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>
            </details>

            <details style={{ marginTop: 10 }}>
              <summary className="mini muted" style={{ cursor: "pointer" }}>
                Steps ({steps.length}) — read-only
              </summary>
              <div style={{ marginTop: 8 }}>
                {steps.map((step) => (
                  <div
                    key={step.stepOrder}
                    className="mini"
                    style={{ padding: "6px 0", borderBottom: "1px solid var(--border, #eee)" }}
                  >
                    <div className="row" style={{ gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <strong>
                        {step.stepOrder}. {step.name}
                      </strong>
                      {step.stepType === "INTERNAL_TRANSFORM" && (
                        <span className="badge mini">{step.transformType ?? "transform"}</span>
                      )}
                      {step.enableRetrieval && <span className="badge mini info">retrieval</span>}
                      {step.promptDocFilename && (
                        <span className="badge mini">📎 {step.promptDocFilename}</span>
                      )}
                      {step.sampleMode !== "none" && (
                        <span className="badge mini">sample: {step.sampleMode}</span>
                      )}
                    </div>
                    {step.inputMappings && step.inputMappings.length > 0 && (
                      <div className="muted" style={{ marginTop: 2 }}>
                        inputs:{" "}
                        {step.inputMappings
                          .map((m) =>
                            m.sourceType === "STEP_OUTPUT"
                              ? `${m.varName} ← step ${m.stepOrder}`
                              : m.varName,
                          )
                          .join(", ")}
                      </div>
                    )}
                    {step.hasPrompt && (
                      <details style={{ marginTop: 4 }}>
                        <summary className="muted" style={{ cursor: "pointer" }}>
                          prompt
                        </summary>
                        <pre
                          style={{
                            whiteSpace: "pre-wrap",
                            maxHeight: 220,
                            overflow: "auto",
                            marginTop: 4,
                          }}
                        >
                          {step.prompt}
                        </pre>
                      </details>
                    )}
                  </div>
                ))}
              </div>
            </details>
          </>
        )}

        <div
          className="row between"
          style={{ marginTop: 18, gap: 10, alignItems: "center", flexWrap: "wrap" }}
        >
          <span className="mini muted">
            {changedCount > 0 ? (
              <>
                {changedCount} setting{changedCount === 1 ? "" : "s"} changed ·{" "}
                <button
                  type="button"
                  className="btn mini"
                  onClick={() => setOverrides({})}
                  disabled={submitting}
                >
                  Reset
                </button>
              </>
            ) : (
              "Using default settings"
            )}
          </span>
          <span className="row" style={{ gap: 8 }}>
            <button type="button" className="btn" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button
              type="button"
              className="btn primary"
              disabled={submitting || !plan}
              onClick={() => onRun(overrides)}
            >
              {submitting
                ? "Queueing…"
                : `Run ${props.targetCount} target${props.targetCount === 1 ? "" : "s"}`}
            </button>
          </span>
        </div>
      </div>
    </div>
  );
}
